import {
  IocSelectorOption,
  IocSelectorService,
} from './../../../../_shared/components/ioc-selector/ioc-selector.service';
import { Injectable, inject } from '@angular/core';
import { Match, Side } from '../../../../_shared/models/match.model';
import {
  ScoreFormat,
  SetType,
} from '../../../../_shared/models/score-format.model';
import { BoardHelpService } from '../../board-help.service';
import {
  Point,
  Server,
  PlayerInfo,
  PlayerCourtPosition,
} from '../../../../_shared/models/point.model';
import { CourtSide } from '../../../../_shared/models/enums/court-side';

@Injectable({
  providedIn: 'root',
})
export class BadmintonTrackerBrainService {
  private iocSelectorService = inject(IocSelectorService);
  private side1PlayerPosition: PlayerCourtPosition = {};
  private side2PlayerPosition: PlayerCourtPosition = {};

  constructor(private bhs: BoardHelpService) {}

  async selectServer(
    match: Match,
    showCancelButton: boolean,
    serverNumber?: number,
    sideNumber?: number | undefined
  ): Promise<Server | undefined> {
    const serverTitle: string = 'Select server';
    const players = this.getPlayers(match);
    const serverOptions = this.getServerOptions(match, players, serverNumber, sideNumber);

    let selectedServer: Server = await this.iocSelectorService.openPopover(
      serverTitle,
      serverOptions,
      showCancelButton
    );

    if (!selectedServer) return undefined;

    const returnerPlayerNumber = match.matchType == 'D' ? await this.selectReturner(
      match,
      selectedServer?.returnerSideNumber,
      showCancelButton
    ): 1;

    if (!returnerPlayerNumber) return undefined;

    selectedServer.returnerPlayerNumber = returnerPlayerNumber;
    this.assignPlayerPositions(players, selectedServer);
    return selectedServer;
  }

  /**
   * Automatically selects the server for singles matches after the first set.
   * In badminton, the side that won the previous game serves first in the next game.
   */
  async autoSelectServerForSinglesAfterFirstSet(match: Match, sideNumber: number): Promise<Server | undefined> {
    const players = this.getPlayers(match);
    const serverPlayer = players?.[sideNumber]?.[0]

    if (!serverPlayer) {
      return undefined;
    }

    const server = {
      "playerName": serverPlayer.playerName,
      "sideNumber": sideNumber,
      "playerNumber": 1,
      "player": serverPlayer.player,
      "returnerSideNumber": sideNumber === 1 ? 2 : 1,
      "returningSide": CourtSide.DEUCE,
      "returnerPlayerNumber": 1
    }
    this.assignPlayerPositions(players, server);
    return server
  }

  /**
 * Updates player court positions in badminton (Singles & Doubles)
 * Called after each rally point update
 *
 * Logic:
 * - If the serving side wins → same side continues serving, server & partner switch court positions
 * - If the receiving side wins → service changes to other team, court positions remain as they were
 * - Court side (right/deuce or left/ad) depends on team's current score parity (even = right / odd = left)
 */
updatePlayerCourtPosition(
  match: Match,
  lastPoint: Point,
  pointWinnerSideNumber: number,
  playerPositionData: { side1: PlayerCourtPosition; side2: PlayerCourtPosition }
): { side1: PlayerCourtPosition; side2: PlayerCourtPosition } {
  const servingSide = lastPoint.server?.sideNumber;
  const matchType = match.matchType; // 'S' (singles) or 'D' (doubles)

  // Get both side scores after the rally
  const side1Score = lastPoint.side1Score ?? 0;
  const side2Score = lastPoint.side2Score ?? 0;

  // Clone to avoid mutating the input reference directly
  const updatedPositions: {
    side1: PlayerCourtPosition;
    side2: PlayerCourtPosition;
  } = {
    side1: { ...playerPositionData.side1 },
    side2: { ...playerPositionData.side2 },
  };

  // 🏸 Case 1: Singles logic
  if (matchType === 'S') {
    // In singles, the server should be positioned based on their score
    // Even score (0, 2, 4, 6...): Serve from RIGHT service court (deuce)
    // Odd score (1, 3, 5, 7...): Serve from LEFT service court (ad)
    
    // Determine which side will be serving next (the winning side)
    const nextServingSide = pointWinnerSideNumber;
    const nextServingSideScore = nextServingSide === 1 ? side1Score : side2Score;
    
    // Position the server based on their score
    const serverShouldBeOnRight = nextServingSideScore % 2 === 0;
    
    // Get the current server player
    const currentServer = updatedPositions[`side${nextServingSide}`].deuce || updatedPositions[`side${nextServingSide}`].ad;
    
    if (currentServer) {
      if (serverShouldBeOnRight) {
        // Server should be on right (deuce) court
        updatedPositions[`side${nextServingSide}`].deuce = currentServer;
        updatedPositions[`side${nextServingSide}`].ad = null;
      } else {
        // Server should be on left (ad) court
        updatedPositions[`side${nextServingSide}`].ad = currentServer;
        updatedPositions[`side${nextServingSide}`].deuce = null;
      }
    }

    return updatedPositions;
  }

  // 🏸 Case 2: Doubles logic
  // -------------------------------------------------
  // Rule Recap:
  // - If serving side wins → same server continues, server & partner switch positions
  // - If receiving side wins → service passes to the other team
  // - Server position is determined by team's score: even = right (deuce), odd = left (ad)
  // -------------------------------------------------
  
  // Determine which side will be serving next (the winning side)
  const nextServingSide = pointWinnerSideNumber;
  const nextServingSideScore = nextServingSide === 1 ? side1Score : side2Score;

  // Determine which player should serve based on score
  // Even score: player 1 serves from right court, player 2 from left court
  // Odd score: player 1 serves from left court, player 2 from right court
  const serverShouldBeOnRight = nextServingSideScore % 2 === 0;
  
  if (servingSide === pointWinnerSideNumber) {
    // Same team continues serving → switch positions within that side
    const temp = updatedPositions[`side${nextServingSide}`].ad;
    updatedPositions[`side${nextServingSide}`].ad = updatedPositions[`side${nextServingSide}`].deuce;
    updatedPositions[`side${nextServingSide}`].deuce = temp;
  } else {
    // Service passes to the other team
    // The server position is determined by their team's score:
    // - Even score: serve from right court (deuce)
    // - Odd score: serve from left court (ad)
    // We need to position the actual server (whoever that is) in the correct court
    
    const side1Player = updatedPositions[`side${nextServingSide}`].deuce;
    const side2Player = updatedPositions[`side${nextServingSide}`].ad;
    
    // Find which player will be serving next for this team
    const lastTimeThisTeamServed = this.findLastTimeTeamServed(match, nextServingSide);
    let nextServerPlayerNumber: number;
    
    if (lastTimeThisTeamServed) {
      // The player who did NOT serve last time should serve next
      nextServerPlayerNumber = lastTimeThisTeamServed.playerNumber === 1 ? 2 : 1;
    } else {
      // If this team hasn't served before in this game, start with player 1
      nextServerPlayerNumber = 1;
    }
    
    // Position the server based on their team's score
    if (serverShouldBeOnRight) {
      // Even score: server should be on right court (deuce)
      if (nextServerPlayerNumber === 1) {
        updatedPositions[`side${nextServingSide}`].deuce = side1Player;
        updatedPositions[`side${nextServingSide}`].ad = side2Player;
      } else {
        updatedPositions[`side${nextServingSide}`].deuce = side2Player;
        updatedPositions[`side${nextServingSide}`].ad = side1Player;
      }
    } else {
      // Odd score: server should be on left court (ad)
      if (nextServerPlayerNumber === 1) {
        updatedPositions[`side${nextServingSide}`].deuce = side2Player;
        updatedPositions[`side${nextServingSide}`].ad = side1Player;
      } else {
        updatedPositions[`side${nextServingSide}`].deuce = side1Player;
        updatedPositions[`side${nextServingSide}`].ad = side2Player;
      }
    }
  }

  return updatedPositions;
}


  private getPlayers(match: Match): PlayerInfo {
    const players: any = {};

    match.sides.forEach((side, i) => {
      players[i + 1] = side.players.map((player, j) => ({
        playerName: this.bhs.getPlayersNames(
          match,
          side.sideNumber,
          'full',
          j + 1
        ),
        sideNumber: i + 1,
        playerNumber: j + 1,
        player: player.participant?._id || null,
      }));
    });

    return players;
  }

  private getServerOptions(match: Match, players: any, serverNumber?: number, sideNumber?: number): IocSelectorOption[] {
    return (Object.values(players) as any[][]).flatMap((sidePlayers: any[], i) =>
      sidePlayers.filter(player=> {
        return !sideNumber || player.sideNumber === sideNumber
      }).map((player) => ({
        label: player.playerName,
        value: {
          ...player,
          serverNumber,
          returnerSideNumber: i === 0 ? 2 : 1,
          returningSide: CourtSide.DEUCE,
        },
      }))
    );
  }

  private async selectReturner(
    match: Match,
    returnerSideNumber: number = 1,
    showCancelButton: boolean
  ): Promise<number> {
    const returnerTitle = 'Select returner';
    const returnerOptions = match.sides[returnerSideNumber - 1].players.map(
      (_, i) => ({
        label:
          this.bhs.getPlayersNames(match, returnerSideNumber, 'full', i + 1) ??
          '',
        value: i + 1,
      })
    );

    return this.iocSelectorService.openPopover(
      returnerTitle,
      returnerOptions,
      showCancelButton
    );
  }

  private assignPlayerPositions(players: any, selectedServer: Server): void {    
    [1, 2].forEach((side) => {
      players[side].forEach((player: PlayerInfo) => {
        const isServerOrReturner =
          selectedServer.sideNumber === side
            ? player.playerNumber === selectedServer.playerNumber
            : player.playerNumber === selectedServer.returnerPlayerNumber;

        this[`side${side}PlayerPosition`][isServerOrReturner ? 'deuce' : 'ad'] =
          player;
      });
    });
  }

  public getPlayerPosition(side: number): PlayerCourtPosition | null {
    return this[`side${side}PlayerPosition`] ?? null;
  }

  /**
   * Badminton server change logic - rally scoring
   * Whoever wins the rally serves next
   * In the next set, whoever won the rally in the previous set serves first
   */
  changeServer(
    match: Match,
    scoreFormat: ScoreFormat,
    pointWinnerSideNumber: number,
    isNewSet?: boolean
  ): { server?: Server; suggestServerChange: boolean } {
    if (match.matchType === 'S') {
      return this.changeServerForSingles(match, pointWinnerSideNumber, isNewSet);
    }

    return this.changeServerForDoubles(match, pointWinnerSideNumber, isNewSet);
  }

  private changeServerForSingles(
    match: Match,
    pointWinnerSideNumber: number,
    isNewSet?: boolean
  ): { server; suggestServerChange } {
    const lastSet = this.bhs.getLastSet(match);
    const lastGame = this.bhs.getLastGame(match);
    const nextToLastGame = this.bhs.getNextToLastGame(match);
    const nextToLastPoint = this.bhs.getNextToLastPoint(match);
    let suggestServerChange = false;

    // Check if it's the beginning of a new set
    if (
      match.sets.length > 1 &&
      lastSet?.games && lastSet.games.length === 1 &&
      lastGame?.points && lastGame.points.length === 1
    ) {
      // Get the previous set winner
      const previousSet = this.bhs.getLastCompletedSet(match);
      const previousSetWinner = previousSet?.winningSide;
      
      // In the next set, whoever won the rally in the previous set serves first
      // But if the same side won the previous set, they continue serving (no change needed)
      if (previousSetWinner && previousSetWinner !== pointWinnerSideNumber) {
        suggestServerChange = true;
      }
    }

    // If we have the previous point, reuse getRallySetServerOnChange to compute a full server
    if (nextToLastPoint) {
      const server = this.getRallySetServerOnChange(
        nextToLastPoint,
        match.sides,
        match.matchType,
        match,
        isNewSet
      );
      return { server, suggestServerChange };
    }

    // In badminton rally scoring, whoever wins the rally serves next
    // In the next set, whoever won the rally in the previous set serves first
    const sideNumber = pointWinnerSideNumber;
    const playerNumber = 1; // Singles always player 1
    const player = match.sides[sideNumber - 1].players[0]?.participant?._id;
    const returnerSideNumber = sideNumber === 1 ? 2 : 1;
    const returnerPlayerNumber = 1; // Singles always player 1
    const returningSide = isNewSet ? CourtSide.DEUCE : CourtSide.DEUCE; // At start of set, serve from deuce side
    const returnerPlayerId = match.sides[returnerSideNumber - 1]?.players?.[0]?.participant?._id ?? null;

    const server = {
      sideNumber,
      playerNumber,
      player,
      returnerSideNumber,
      returnerPlayerNumber,
      returningSide,
      returnerPlayerId,
    };

    return { server, suggestServerChange };
  }

  private changeServerForDoubles(
    match: Match,
    pointWinnerSideNumber: number,
    isNewSet?: boolean
  ): { server; suggestServerChange } {
    const lastSet = this.bhs.getLastSet(match);
    const lastGame = this.bhs.getLastGame(match);
    const nextToLastGame = this.bhs.getNextToLastGame(match);
    const nextToLastPoint = this.bhs.getNextToLastPoint(match);
    let suggestServerChange = false;

    // Check if it's the beginning of a new set
    if (
      match.sets.length > 1 &&
      lastSet?.games && lastSet.games.length === 1 &&
      lastGame?.points && lastGame.points.length === 1
    ) {
      // Get the previous set winner
      const previousSet = this.bhs.getLastCompletedSet(match);
      const previousSetWinner = previousSet?.winningSide;
      
      // In the next set, whoever won the rally in the previous set serves first
      // But if the same side won the previous set, they continue serving (no change needed)
      if (previousSetWinner && previousSetWinner !== pointWinnerSideNumber) {
        suggestServerChange = true;
      }
    }

     // If we have the previous point, reuse getRallySetServerOnChange to compute a full server
    if (nextToLastPoint) {
      const server = this.getRallySetServerOnChange(
        nextToLastPoint,
        match.sides,
        match.matchType,
        match,
        isNewSet
      );
      return { server, suggestServerChange };
    }

    let sideNumber = pointWinnerSideNumber;
    let playerNumber: number = 1;
    const player = match.sides[sideNumber - 1].players[playerNumber - 1]?.participant?._id || '';
    const returnerSideNumber = sideNumber === 1 ? 2 : 1;
    const returnerPlayerNumber = 1;
    const returningSide = CourtSide.DEUCE;
    const returnerPlayerId = match.sides[returnerSideNumber - 1]?.players?.[returnerPlayerNumber - 1]?.participant?._id ?? null;

    const server = {
      sideNumber,
      playerNumber,
      player,
      returnerSideNumber,
      returnerPlayerNumber,
      returningSide,
      returnerPlayerId,
    };

    return { server, suggestServerChange };
  }

  /**
   * Check if a game is won in badminton
   */
  isAddedGame(
    match: Match,
    scoreFormat: ScoreFormat,
    nextToLastPointNumberOfPointsWonForWinner: number,
    nextToLastPointNumberOfPointsWonForLoser: number,
    winningSide: number
  ): boolean {
    const numberOfPointsInGame = scoreFormat.numberOfPointsInGame || 21;
    const currentWinnerScore = nextToLastPointNumberOfPointsWonForWinner + 1;
    const currentLoserScore = nextToLastPointNumberOfPointsWonForLoser;

    // Badminton scoring rules:
    // - Games to 21 points, win by 2
    // - Cap at 30 points (first to 30 wins) - if cap is specified
    // - Games to 15 points, win by 2, cap at 20 - if cap is specified
    // - Games to 11 points, win by 2, cap at 13 - if cap is specified
    // - If no cap is specified, games continue until win by 2

    // Check if a cap is specified in the score format
    if (scoreFormat.capOfPoints) {
      // Check if reached the specified cap
      if (currentWinnerScore >= scoreFormat.capOfPoints) {
        return true;
      }
    }

    // Check if reached minimum points and won by 2
    if (currentWinnerScore >= numberOfPointsInGame && 
        currentWinnerScore >= currentLoserScore + 2) {
      return true;
    }

    return false;
  }

  getSetType(match: Match): SetType {
    // Badminton always uses rally scoring
    return SetType.RALLY;
  }

  isLastSet(match: Match, scoreFormat: ScoreFormat): boolean {
    return match.sets.length === scoreFormat.numberOfSets;
  }

  /**
   * Find the last time a specific team served in the current game
   */
  private findLastTimeTeamServed(match: Match, teamSideNumber: number): { playerNumber: number } | null {
    const lastGame = this.bhs.getLastGame(match);
    if (!lastGame?.points) return null;
    
    // Search backwards through points to find the last time this team served
    for (let i = lastGame.points.length - 1; i >= 0; i--) {
      const point = lastGame.points[i];
      if (point.server?.sideNumber === teamSideNumber) {
        return { playerNumber: point.server.playerNumber || 1 };
      }
    }
    
    return null;
  }

  /**
   * Get server for rally scoring (badminton always uses rally scoring)
   */
  getRallySetServerOnChange(
    nextToLastPoint: Point,
    sides: Side[],
    matchType: string,
    match: Match,
    isNewSet?: boolean
  ): Server {
    const serverSideNumber = nextToLastPoint.winningSide || 1;
    const returnerSideNumber = serverSideNumber === 1 ? 2 : 1;

    // Determine returner using lastPoint.playerPositions if available (prefer stable court mapping)
    const playerPositions = nextToLastPoint.playerPositions || ({} as any);
    const returnerPos: PlayerCourtPosition | undefined = (playerPositions as any)[`side${returnerSideNumber}`];
    const currentPos: PlayerCourtPosition | undefined = (playerPositions as any)[`side${serverSideNumber}`];
    
    // Determine court side based on returner's side score
    const returningSide: CourtSide = isNewSet ? CourtSide.DEUCE :
      (serverSideNumber === 1 && (nextToLastPoint.side1Score || 0) % 2 === 0) ||
      (serverSideNumber === 2 && (nextToLastPoint.side2Score || 0) % 2 === 0)
        ? CourtSide.DEUCE
        : CourtSide.AD;
        
    let serverPlayerNumber: number;
    let returnerPlayerNumber: number;
    let returnerPlayerId: string | undefined = undefined;

    if (matchType === 'S') {
      serverPlayerNumber = 1;
      returnerPlayerNumber = 1;
    } else {
      // Doubles: correct mapping — even (DEUCE/right) => player 1, odd (AD/left) => player 2
      const serverPlayerInfo: any = currentPos[returningSide.toLocaleLowerCase()];
      serverPlayerNumber = serverPlayerInfo?.playerNumber ?? serverPlayerInfo.playerNumber ?? 1;
    }

    // Resolve server player id defensively
    const serverPlayerId =
      sides[serverSideNumber - 1]?.players?.[serverPlayerNumber - 1]?.participant?._id ?? undefined;
    if (returnerPos) {
      // returningSide is enum; keys stored as 'deuce'|'ad'
      const posKey = returningSide === CourtSide.DEUCE ? 'deuce' : 'ad';
      const returnerInfo: any = returnerPos[posKey];
      if (returnerInfo) {
        // Player info shape may differ: prefer playerNumber, then participant._id, then player id
        returnerPlayerNumber = returnerInfo.playerNumber ?? returnerInfo.playerNumber ?? 1;
        returnerPlayerId =
          returnerInfo.participant?._id ?? returnerInfo.player ?? returnerInfo._id ?? undefined;
      } else {
        // fallback: pick first player on returner side
        returnerPlayerNumber = 1;
        returnerPlayerId =
          sides[returnerSideNumber - 1]?.players?.[0]?.participant?._id ?? undefined;
      }
    } else {
      // no positions available -> fallback to simple mapping: use same index as serverPlayerNumber if possible
      returnerPlayerNumber = serverPlayerNumber;
      returnerPlayerId =
        sides[returnerSideNumber - 1]?.players?.[returnerPlayerNumber - 1]?.participant?._id ?? undefined;
    }

    return {
      sideNumber: serverSideNumber,
      playerNumber: serverPlayerNumber,
      returnerSideNumber,
      returnerPlayerNumber,
      returningSide,
      player: serverPlayerId,
      returnerPlayerId
    };
  }

   swapSides(match: Match): void {
      let isReverse = !this.bhs.getLastPoint(match)!.extensions!.find((ext) => ext.name === 'areSidesReversed')!.value;
      if (this.bhs.getLastPoint(match)?.extensions?.find((ext) => ext.name === 'areSidesReversed')) {        
        this.bhs.getLastPoint(match)!.extensions!.find((ext) => ext.name === 'areSidesReversed')!.value = isReverse;
      }
  }
}


